import React, { memo, useCallback, useMemo } from "react";
import { Container } from "@pixi/react";
import { Settings } from "../../settings";
import { useDimensions } from "../../Contexts/DimensionsContext";
import { useReelRefs } from "../../Contexts/ReelRefsContext";
import HoldAndWinTensionOverlay from "./HoldAndWinTensionOverlay";

/**
 * Hold and Win Tension Overlay Set
 * Creates a grid of tension overlays (5 reels × 4 symbols = 20 total)
 * Each overlay sits on top of its corresponding symbol position
 * Manages ONLY tension animations - completely independent from multiplier animations
 */
const HoldAndWinTensionOverlaySet = memo(() => {
  const { symbolWidth, symbolHeight, reelSpacing } = useDimensions();
  const { holdAndWinTensionOverlayRefs } = useReelRefs();

  // Callback ref to register tension overlay with context
  const setTensionOverlayRef = useCallback(
    (reelIndex, symbolIndex) => (el) => {
      if (holdAndWinTensionOverlayRefs.current) {
        if (!holdAndWinTensionOverlayRefs.current[reelIndex]) {
          holdAndWinTensionOverlayRefs.current[reelIndex] = [];
        }
        holdAndWinTensionOverlayRefs.current[reelIndex][symbolIndex] = el;
      }
    },
    [holdAndWinTensionOverlayRefs]
  );

  // Memoize container Y position
  const containerY = useMemo(
    () => -symbolHeight * (0.5 * Settings.SYMBOLS_VISIBLE - 0.5),
    [symbolHeight]
  );

  // Create grid of tension overlays: 5 reels × 4 symbols
  // Positioning MUST match HoldAndWinOverlayReel exactly
  const tensionOverlays = useMemo(() => {
    const overlays = [];
    for (let reelIndex = 0; reelIndex < Settings.REEL_COUNT; reelIndex++) {
      for (
        let symbolIndex = 0;
        symbolIndex < Settings.SYMBOLS_VISIBLE;
        symbolIndex++
      ) {
        // Match HoldAndWinOverlayReel positioning exactly:
        // - X: reelSpacing * (index - 2) to account for divider widths
        // - Y: symbolHeight * idx + idx (has +idx offset)
        const xPosition = reelSpacing * (reelIndex - 2);
        const yPosition = symbolHeight * symbolIndex + symbolIndex;

        overlays.push(
          <Container
            key={`${reelIndex}-${symbolIndex}`}
            name={`tension_overlay_container_${reelIndex}_${symbolIndex}`}
            x={xPosition}
            y={yPosition}
          >
            <HoldAndWinTensionOverlay
              ref={setTensionOverlayRef(reelIndex, symbolIndex)}
              reelIndex={reelIndex}
              symbolIndex={symbolIndex}
              symbolWidth={symbolWidth}
              symbolHeight={symbolHeight}
            />
          </Container>
        );
      }
    }
    return overlays;
  }, [symbolWidth, symbolHeight, reelSpacing, setTensionOverlayRef]);

  return (
    <Container
      name="hold_and_win_tension_overlay_set"
      zIndex={8}
      x={0}
      y={containerY}
    >
      {tensionOverlays}
    </Container>
  );
});

export default HoldAndWinTensionOverlaySet;
